import * as React from 'react';
import {
  Box,
  Button,
  IconButton,
  InputProps,
  UnorderedList,
  ListItem,
  useFormControl,
  useMultiStyleConfig,
  useToast,
  Icon,
  Flex,
} from '@chakra-ui/react';
import {
  useDropzone,
  FileWithPath,
  Accept,
  FileRejection,
} from 'react-dropzone';
import { FiTrash } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

export type InputFileWithoutUploadType = {
  file?: File | null;
  hasExistingFile?: boolean;
  accept?: Accept;
  minSize?: number;
  maxSize?: number;
  isDisabled?: boolean;
  isInvalid?: boolean;
  onChange: (file?: File) => void;
};

/**
 * A component for selecting a single file WITHOUT immediately uploading it to the server.
 * The file is held locally as a File object and is intended to be sent to the API
 * only along with the rest of the form values in a single submission (unlike InputFileWithUpload/InputFileBasket
 * which upload the file immediately upon selection).
 */
export const InputFileWithoutUpload = ({
  file,
  hasExistingFile,
  isDisabled,
  accept,
  onChange,
  size,
  variant,
  minSize,
  maxSize,
  isInvalid,
  ...props
}: InputFileWithoutUploadType & InputProps): JSX.Element => {
  const { t } = useTranslation();
  const toast = useToast({
    position: 'bottom',
    status: 'error',
    isClosable: true,
  });
  const chakraInputProps = useFormControl(props);
  const chakraInputStyle = useMultiStyleConfig('InputFile', {
    size,
    variant,
  });

  const handleAcceptedFiles = (acceptedFiles: FileWithPath[]) => {
    if (acceptedFiles.length) {
      onChange(acceptedFiles[0]);
    }
  };

  const handleRejectedFiles = (fileRejections: FileRejection[]) => {
    fileRejections.forEach(fileRejection => {
      toast({
        title: t('inputFileDragAndDrop.uploadError', 'Plik odrzucony'),
        description: (
          <UnorderedList>
            {fileRejection.errors.map(error => (
              <ListItem key={error.code}>{error.message}</ListItem>
            ))}
          </UnorderedList>
        ),
      });
    });
  };

  const handleClearFile = () => {
    onChange(undefined);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDropAccepted: handleAcceptedFiles,
    onDropRejected: handleRejectedFiles,
    maxFiles: 1,
    disabled: isDisabled,
    accept,
    minSize,
    maxSize,
  });

  return (
    <Flex alignContent='stretch' alignItems='center' {...props}>
      <Box
        flex='auto'
        __css={chakraInputStyle.field}
        {...chakraInputProps}
        {...getRootProps()}
        borderColor={isInvalid ? 'red.500' : undefined}
      >
        <input {...getInputProps()} />
        <Box __css={chakraInputStyle.heading}>
          {file?.name ||
            (hasExistingFile &&
              t(
                'materials.testFileAttached',
                'Plik załączony — wybór nowego go zastąpi',
              ))}
        </Box>
        <Button
          colorScheme='gray'
          size='sm'
          sx={chakraInputStyle.action}
          isDisabled={isDisabled}
        >
          {t('selectFile', 'Wybierz plik')}
        </Button>
      </Box>
      {file && (
        <IconButton
          m={2}
          aria-label={t('deleteFile', 'Usuń plik')}
          onClick={handleClearFile}
          icon={<Icon as={FiTrash} />}
          isDisabled={isDisabled}
        />
      )}
    </Flex>
  );
};
